/**
 * Eira Theme — Quick View
 * Fetches product content via /products/HANDLE?section_id=quick-view
 * and displays it in a modal overlay.
 */
(function () {
  'use strict';

  var MODAL_ID = 'BcQuickView';
  var SECTION_ID = 'quick-view';

  /* ── Focus trap utility ────────────────────────────────── */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var lastFocusedElement = null;

  function trapFocus(container) {
    var focusables = container.querySelectorAll(FOCUSABLE);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    container._focusTrapHandler = function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    container.addEventListener('keydown', container._focusTrapHandler);
  }

  function releaseFocus(container) {
    if (container._focusTrapHandler) {
      container.removeEventListener('keydown', container._focusTrapHandler);
      delete container._focusTrapHandler;
    }
  }

  /* ── Create modal shell (once) ─────────────────────────── */
  function getOrCreateModal() {
    var existing = document.getElementById(MODAL_ID);
    if (existing) return existing;

    var shell = document.createElement('div');
    shell.id = MODAL_ID;
    shell.className = 'bc-qv';
    shell.setAttribute('role', 'dialog');
    shell.setAttribute('aria-modal', 'true');
    shell.setAttribute('aria-label', 'Quick view');
    shell.innerHTML = [
      '<div class="bc-qv__backdrop" data-qv-close></div>',
      '<div class="bc-qv__modal">',
      '  <button type="button" class="bc-qv__close" data-qv-close aria-label="Close quick view">',
      '    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>',
      '  </button>',
      '  <div class="bc-qv__body">',
      '    <div class="bc-qv__loading"><div class="bc-qv__spinner"></div></div>',
      '  </div>',
      '</div>'
    ].join('');

    document.body.appendChild(shell);
    return shell;
  }

  /* ── Open modal ────────────────────────────────────────── */
  function open(productUrl) {
    var modal = getOrCreateModal();
    var body = modal.querySelector('.bc-qv__body');

    /* Save focus for restoration on close */
    lastFocusedElement = document.activeElement;

    /* Show loading state */
    body.innerHTML = '<div class="bc-qv__loading"><div class="bc-qv__spinner"></div></div>';
    modal.classList.add('is-open');
    document.body.classList.add('bc-qv-open');

    /* Fetch product section */
    var fetchUrl = productUrl + (productUrl.includes('?') ? '&' : '?') + 'section_id=' + SECTION_ID;

    fetch(fetchUrl)
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = doc.querySelector('.bc-qv__content');

        /* Fix #3 — Validate section has actual content, not just an empty wrapper */
        var hasContent = section && section.children.length > 0;
        if (!hasContent) {
          body.innerHTML = '<div class="bc-qv__loading" style="flex-direction:column;gap:12px;"><p style="color:var(--color-muted);font-size:14px;">Could not load product. The section may be unavailable.</p></div>';
          return;
        }

        body.innerHTML = '';
        body.appendChild(section);

        /* Propagate color scheme class from section to modal shell */
        var schemeClass = section.getAttribute('data-color-scheme');
        if (schemeClass) {
          var modalEl = modal.querySelector('.bc-qv__modal');
          if (modalEl) {
            /* Remove any previous scheme class */
            modalEl.className = modalEl.className.replace(/\bcolor-\S+/g, '').trim();
            modalEl.classList.add(schemeClass);
          }
        }

        /* Parse variants JSON */
        var variantsScript = doc.querySelector('[data-qv-variants]');
        if (variantsScript) {
          var variantsJson = document.createElement('script');
          variantsJson.type = 'application/json';
          variantsJson.dataset.qvVariants = '';
          variantsJson.textContent = variantsScript.textContent;
          body.appendChild(variantsJson);
        }

        initInteractions(modal);

        /* Fix #5 — Move focus into modal and activate focus trap */
        setTimeout(function () {
          var closeBtn = modal.querySelector('.bc-qv__close');
          if (closeBtn) closeBtn.focus();
          trapFocus(modal);
        }, 100);
      })
      .catch(function () {
        body.innerHTML = '<div class="bc-qv__loading" style="flex-direction:column;gap:12px;"><p style="color:var(--color-muted);font-size:14px;">Something went wrong. Please try again.</p></div>';
      });
  }

  /* ── Close modal ───────────────────────────────────────── */
  function close() {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('bc-qv-open');

    /* Release focus trap and restore focus */
    releaseFocus(modal);
    if (lastFocusedElement && lastFocusedElement.focus) {
      lastFocusedElement.focus();
      lastFocusedElement = null;
    }
  }

  /* ── Shopify CDN safe image src swap (modern query-param approach) ── */
  function getSizedImageUrl(src, width) {
    if (!src) return '';
    /* Use shared utility if available, otherwise inline fallback */
    if (window.EiraCart && window.EiraCart.getSizedImageUrl) {
      return window.EiraCart.getSizedImageUrl(src, width);
    }
    try {
      var url = new URL(src, 'https://cdn.shopify.com');
      url.searchParams.set('width', width);
      url.searchParams.delete('height');
      return url.toString().replace('https://cdn.shopify.com', '');
    } catch (e) {
      var sep = src.indexOf('?') !== -1 ? '&' : '?';
      return src + sep + 'width=' + width;
    }
  }

  /* ── Init interactions inside loaded content ───────────── */
  function initInteractions(modal) {
    var body = modal.querySelector('.bc-qv__body');
    if (!body) return;

    /* Thumbnail click — update src AND srcset so browser shows the new image */
    var thumbs = body.querySelectorAll('[data-qv-thumb]');
    var mainImg = body.querySelector('#QvMainImgTag');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var url = this.dataset.imageUrl;
        if (mainImg && url) {
          mainImg.setAttribute('src', getSizedImageUrl(url, 800));
          mainImg.setAttribute('srcset', getSizedImageUrl(url, 400) + ' 400w, ' + getSizedImageUrl(url, 600) + ' 600w, ' + getSizedImageUrl(url, 800) + ' 800w');
        }
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');
      });
    });

    /* Quantity stepper */
    var minusBtn = body.querySelector('[data-qv-qty-minus]');
    var plusBtn = body.querySelector('[data-qv-qty-plus]');
    var qtyInput = body.querySelector('[data-qv-qty-input]');

    if (minusBtn && plusBtn && qtyInput) {
      minusBtn.addEventListener('click', function () {
        var val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, val - 1);
      });
      plusBtn.addEventListener('click', function () {
        var val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
      });
    }

    /* Variant selection */
    var variantsEl = body.querySelector('[data-qv-variants]');
    var variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) { /* ignore */ }
    }

    var pills = body.querySelectorAll('[data-qv-option-index]');
    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var idx = parseInt(this.dataset.qvOptionIndex, 10);
        /* Deactivate siblings */
        var siblings = body.querySelectorAll('[data-qv-option-index="' + idx + '"]');
        siblings.forEach(function (s) { s.classList.remove('is-active'); });
        this.classList.add('is-active');

        /* Find matching variant */
        var selectedOptions = [];
        var groups = body.querySelectorAll('.bc-qv__variant-group');
        groups.forEach(function (g) {
          var active = g.querySelector('.bc-qv__variant-pill.is-active');
          if (active) selectedOptions.push(active.dataset.qvValue);
        });

        /* Fix #6 — Fallback to option1/option2/option3 if options array is missing */
        var matchedVariant = variants.find(function (v) {
          var opts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
          return opts.every(function (opt, i) {
            return opt === selectedOptions[i];
          });
        });

        if (matchedVariant) {
          /* Update hidden input */
          var idInput = body.querySelector('[data-qv-variant-id]');
          if (idInput) idInput.value = matchedVariant.id;

          /* Update price */
          var priceEl = body.querySelector('.bc-qv__price-current');
          if (priceEl) priceEl.textContent = formatMoney(matchedVariant.price);

          var compareEl = body.querySelector('.bc-qv__price-compare');
          if (compareEl) {
            if (matchedVariant.compare_at_price > matchedVariant.price) {
              compareEl.textContent = formatMoney(matchedVariant.compare_at_price);
              compareEl.style.display = '';
            } else {
              compareEl.style.display = 'none';
            }
          }

          /* Update ATC button state */
          var atcBtn = body.querySelector('.bc-qv__atc');
          if (atcBtn) {
            if (matchedVariant.available) {
              atcBtn.disabled = false;
              atcBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg> ADD TO CART';
            } else {
              atcBtn.disabled = true;
              atcBtn.innerHTML = 'SOLD OUT';
            }
          }

          /* Update image if variant has one — update src + srcset */
          if (matchedVariant.featured_image && mainImg) {
            var variantImgSrc = matchedVariant.featured_image.src;
            mainImg.setAttribute('src', getSizedImageUrl(variantImgSrc, 800));
            mainImg.setAttribute('srcset', getSizedImageUrl(variantImgSrc, 400) + ' 400w, ' + getSizedImageUrl(variantImgSrc, 600) + ' 600w, ' + getSizedImageUrl(variantImgSrc, 800) + ' 800w');
          }
        }
      });
    });

    /* ATC form — intercept for AJAX if cart drawer is present */
    var form = body.querySelector('[data-qv-form]');

    /* Share button */
    var shareBtn = body.querySelector('[data-qv-share]');
    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var title = this.getAttribute('data-share-title') || document.title;
        var url = this.getAttribute('data-share-url') || window.location.href;
        var label = this.querySelector('[data-share-label]');
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () {
            if (label) {
              var orig = label.textContent;
              label.textContent = 'Link Copied!';
              setTimeout(function () { label.textContent = orig; }, 2000);
            }
          });
        } else {
          window.prompt('Copy this link:', url);
        }
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        var drawer = document.getElementById('CartDrawer');
        if (!drawer) return; /* Let default submit happen (page cart mode) */

        e.preventDefault();
        var formData = new FormData(form);
        var id = parseInt(formData.get('id'), 10);
        var qty = parseInt(formData.get('quantity'), 10) || 1;
        if (!id) return;

        var atcBtn2 = form.querySelector('.bc-qv__atc');
        var origHtml = atcBtn2 ? atcBtn2.innerHTML : '';
        if (atcBtn2) { atcBtn2.disabled = true; atcBtn2.textContent = 'Adding…'; }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: qty }] })
        })
          .then(function (r) { return r.json(); })
          .then(function () { return fetch('/cart.js').then(function (r2) { return r2.json(); }); })
          .then(function (cart) {
            if (atcBtn2) { atcBtn2.innerHTML = '✓ Added!'; }
            /* Update header badge */
            document.querySelectorAll('.bc-header__cart-count').forEach(function (el) {
              el.textContent = cart.item_count;
              el.style.display = cart.item_count > 0 ? '' : 'none';
            });
            document.querySelectorAll('.bc-cart-drawer__count-badge').forEach(function (el) {
              el.textContent = cart.item_count;
            });

            setTimeout(function () {
              close();
              /* Fix #4 — Dispatch event instead of directly mutating cart drawer classes */
              document.dispatchEvent(new CustomEvent('eira:cart:open'));
            }, 600);
          })
          .catch(function () {
            if (atcBtn2) { atcBtn2.innerHTML = origHtml; atcBtn2.disabled = false; }
          });
      });
    }
  }

  /* ── Format money helper — Fix #2: Shopify multi-currency support ── */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    var moneyFormat = window.theme && window.theme.moneyFormat
      ? window.theme.moneyFormat
      : (window.Shopify && window.Shopify.money_format
        ? window.Shopify.money_format
        : '${{amount}}');

    var value = cents / 100;

    function addThousandsSep(numStr, sep) {
      var parts = numStr.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return parts.join('.');
    }

    var amount = addThousandsSep(value.toFixed(2), ',');
    var amountNoDecimals = addThousandsSep(Math.round(value).toString(), ',');
    var amountWithComma = addThousandsSep(value.toFixed(2), '.').replace(/\.(\d{2})$/, ',$1');
    var amountNoDecimalsWithComma = addThousandsSep(Math.round(value).toString(), '.');
    var amountWithApostrophe = addThousandsSep(value.toFixed(2), "'");
    var amountWithSpace = addThousandsSep(value.toFixed(2), ' ');
    var amountNoDecimalsWithSpace = addThousandsSep(Math.round(value).toString(), ' ');

    return moneyFormat
      .replace(/\{\{amount_with_comma_separator\}\}/g, amountWithComma)
      .replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g, amountNoDecimalsWithComma)
      .replace(/\{\{amount_with_apostrophe_separator\}\}/g, amountWithApostrophe)
      .replace(/\{\{amount_no_decimals_with_space_separator\}\}/g, amountNoDecimalsWithSpace)
      .replace(/\{\{amount_with_space_separator\}\}/g, amountWithSpace)
      .replace(/\{\{amount_no_decimals\}\}/g, amountNoDecimals)
      .replace(/\{\{amount\}\}/g, amount);
  }

  /* ── Global event listeners ────────────────────────────── */
  document.addEventListener('click', function (e) {
    /* Open trigger */
    var trigger = e.target.closest('[data-quick-view]');
    if (trigger) {
      e.preventDefault();
      var url = trigger.dataset.quickView || trigger.dataset.productUrl;
      if (url) open(url);
      return;
    }

    /* Close triggers */
    if (e.target.closest('[data-qv-close]')) {
      e.preventDefault();
      close();
    }
  });

  /* Escape key */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });
})();
